import { useState, type FormEvent } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Center,
  Group,
  PasswordInput,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconShieldLock } from "@tabler/icons-react";
import { useAuth } from "../../auth/AuthContext.js";
import { friendlyError } from "../../api/client.js";
import { Logo } from "../../components/Logo.js";
import { ThemeToggle } from "../../components/ThemeToggle.js";

export function AdminLogin() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (auth.status === "authenticated" && auth.session?.role === "admin") {
    return <Navigate to="/admin" replace />;
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    try {
      const role = await auth.login(username.trim(), password);
      notifications.show({ color: "green", message: "Signed in" });
      navigate(role === "admin" ? "/admin" : "/", { replace: true });
    } catch (err) {
      notifications.show({
        color: "red",
        title: "Sign-in failed",
        message: friendlyError(err),
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Center mih="100vh" p="md" style={{ position: "relative" }}>
      {/* Theme toggle in the corner */}
      <Box style={{ position: "absolute", top: 16, right: 16 }}>
        <ThemeToggle />
      </Box>

      <Stack w={{ base: 320, sm: 380 }} gap="md">
        <Stack gap={4} align="center" mb="xs">
          <Logo variant="admin" size={64} />
          <Group gap={6} mt="xs">
            <IconShieldLock size={14} stroke={1.75} />
            <Text size="xs" c="dimmed" tt="uppercase" fw={600} style={{ letterSpacing: "0.06em" }}>
              Restricted area
            </Text>
          </Group>
        </Stack>

        <Box
          component="form"
          onSubmit={onSubmit}
          p="xl"
          style={{
            borderRadius: 12,
            border: "1px solid var(--mantine-color-default-border)",
            background: "var(--mantine-color-body)",
          }}
        >
          <Stack gap="sm">
            <TextInput
              label="Username"
              value={username}
              onChange={(e) => setUsername(e.currentTarget.value)}
              required
              autoComplete="username"
              autoFocus
            />
            <PasswordInput
              label="Password"
              value={password}
              onChange={(e) => setPassword(e.currentTarget.value)}
              required
              autoComplete="current-password"
            />
            <Button type="submit" loading={submitting} fullWidth size="md" mt="xs">
              Sign in to admin
            </Button>
          </Stack>
        </Box>

        <Text size="xs" c="dimmed" ta="center">
          Operator access only. All actions are audited.
        </Text>
      </Stack>
    </Center>
  );
}
